import { Injectable, inject, signal, computed } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../environments/environment';
import { jkuIdpAlias } from '../auth/login-options';

/** Known JKU `user_type` synonyms. Matched by equality or substring, checked student → employee → external. */
const STUDENT_TOKENS = ['student', 'studierend'];
const EMPLOYEE_TOKENS = ['staff', 'intern', 'employee', 'mitarbeiter', 'bedienst', 'faculty', 'personal'];
const EXTERNAL_TOKENS = ['extern', 'partner', 'guest', 'gast'];

function matchesAny(value: string, tokens: string[]): boolean {
  return tokens.some(token => value === token || value.includes(token));
}

/**
 * True for a JKU account number: `AK` + digits for staff, `K` + digits for students
 * (e.g. `AK123456`, `K12345678`). Case-insensitive — Keycloak lowercases values that pass through
 * usernames. Mirrors `ClassifyMyParkerFromSsoHandler.IsJkuAccount` in the backend.
 */
export function isJkuAccount(value: unknown): boolean {
  return typeof value === 'string' && /^a?k\d+$/i.test(value.trim());
}

/**
 * Dev-only stand-in for the JKU `user_type` claim.
 *
 * Local Keycloak accounts never carry `user_type` — it is injected by the JKU IdP mappers — so
 * without this the JKU theme and the SSO-guarded webshop are unreachable on a dev machine.
 * Returns '' (i.e. no simulation) unless the environment opts in, and is hard-disabled in
 * production regardless of what the environment file says.
 *
 * `?jku_user_type=staff` overrides the configured value for a session, so the student and
 * employee variants of the copy can both be checked.
 *
 * Also what makes the login-time classification trigger fire (ParkerProvisioningService), so a dev
 * session can exercise the classify-me call and not only the copy. UI side only: the backend still
 * reads its own claims from the token.
 */
export function simulatedUserType(): string {
  const jku = (environment as { production: boolean; jku?: { simulateSsoUser?: boolean; simulatedUserType?: string } }).jku;
  if (environment.production || !jku?.simulateSsoUser) {
    return '';
  }
  const override = new URLSearchParams(window.location.search).get('jku_user_type');
  return (override || jku.simulatedUserType || 'student').trim();
}

/**
 * Reads the JKU-relevant claims from the Keycloak token (delivered via the
 * `jku-attributes` client scope + IdP mappers) and exposes them as signals.
 * Claims: user_type (e.g. "student" | "staff" | "extern" — vocabulary not fully stable, and blank
 * for some accounts), user_status, given_name, family_name, email, jku_account (JKU account number,
 * e.g. AK123456 — imported from the JKU IdP's preferred_username).
 *
 * jku_account rather than the token's own preferred_username: that one is the Keycloak username,
 * which for an account linked to an existing local user is not the JKU number.
 */
@Injectable({ providedIn: 'root' })
export class JkuUserService {
  private readonly keycloak = inject(Keycloak);

  constructor() {
    const kc = this.keycloak;
    // Preserve handlers set by keycloak-angular during APP_INITIALIZER, then sync our signals.
    const prevAuthSuccess = kc.onAuthSuccess;
    kc.onAuthSuccess = () => { prevAuthSuccess?.(); this.isAuthenticated.set(true); this.refreshClaims(); };
    const prevAuthLogout = kc.onAuthLogout;
    kc.onAuthLogout = () => { prevAuthLogout?.(); this.isAuthenticated.set(false); this.clearClaims(); };
    const prevAuthRefreshSuccess = kc.onAuthRefreshSuccess;
    kc.onAuthRefreshSuccess = () => { prevAuthRefreshSuccess?.(); this.isAuthenticated.set(true); this.refreshClaims(); };
  }

  readonly isAuthenticated = signal<boolean>(this.keycloak.authenticated ?? false);
  readonly email = signal<string>(this.claim('email'));
  readonly givenName = signal<string>(this.claim('given_name'));
  readonly familyName = signal<string>(this.claim('family_name'));
  readonly userType = signal<string>(this.claim('user_type') || simulatedUserType());
  readonly userStatus = signal<string>(this.claim('user_status'));
  readonly jkuAccount = signal<string>(this.claim('jku_account'));

  readonly fullName = computed(() =>
    `${this.givenName()} ${this.familyName()}`.trim());

  /**
   * Coarse classification derived from the raw JKU `user_type` claim. The vocabulary isn't
   * fully stable (e.g. employees come through as `staff`, sometimes `intern`), so we match a
   * set of known synonyms/substrings rather than a single literal:
   *   - student  ← "student", "studierende(r)", …
   *   - employee ← "staff", "intern", "employee", "mitarbeiter", "bedienstete(r)", …
   *   - external ← "extern", "external", "partner", "guest", …
   * A JKU account with an empty `user_type` is `external` — the backend classifies it as Other.
   * Anything else (or empty without a JKU account) falls back to `unknown`.
   */
  readonly userTypeCategory = computed<'student' | 'employee' | 'external' | 'unknown'>(() => {
    const value = this.userType().trim().toLowerCase();
    if (!value) return isJkuAccount(this.jkuAccount()) ? 'external' : 'unknown';
    if (matchesAny(value, STUDENT_TOKENS)) return 'student';
    if (matchesAny(value, EMPLOYEE_TOKENS)) return 'employee';
    if (matchesAny(value, EXTERNAL_TOKENS)) return 'external';
    return 'unknown';
  });

  readonly isStudent = computed(() => this.userTypeCategory() === 'student');

  readonly isEmployee = computed(() => this.userTypeCategory() === 'employee');

  /** i18n key for a human-readable user-type label, e.g. `JKU.USER_TYPE.STUDENT`. */
  readonly userTypeLabelKey = computed(() =>
    `JKU.USER_TYPE.${this.userTypeCategory().toUpperCase()}`);

  /**
   * True when the session was established through the JKU SSO broker.
   * `jku_account` and `user_type` are JKU-only claims (delivered via the `jku-attributes` scope +
   * IdP mappers), so both are empty for local / self-registered parkers. The account number is the
   * primary signal: JKU sends it for everyone, while `user_type` is blank for some accounts.
   */
  readonly isJkuSso = computed(() =>
    this.isAuthenticated() && (isJkuAccount(this.jkuAccount()) || this.userType().trim() !== ''));

  /** Sends the user straight to the JKU IdP, skipping the Keycloak login page. */
  loginWithJku(): void {
    this.keycloak.login({
      idpHint: jkuIdpAlias(),
      redirectUri: window.location.href
    });
  }

  logout(): void {
    this.keycloak.logout({ redirectUri: window.location.origin + '/JKU-webshop' });
  }

  private refreshClaims(): void {
    this.email.set(this.claim('email'));
    this.givenName.set(this.claim('given_name'));
    this.familyName.set(this.claim('family_name'));
    this.userType.set(this.claim('user_type') || simulatedUserType());
    this.userStatus.set(this.claim('user_status'));
    this.jkuAccount.set(this.claim('jku_account'));
  }

  private clearClaims(): void {
    this.email.set('');
    this.givenName.set('');
    this.familyName.set('');
    this.userType.set('');
    this.userStatus.set('');
    this.jkuAccount.set('');
  }

  private claim(name: string): string {
    const parsed = this.keycloak.tokenParsed as Record<string, unknown> | undefined;
    const value = parsed?.[name];
    return typeof value === 'string' ? value : '';
  }
}
